import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Switch,
  Divider,
  TablePagination,
  InputAdornment,
  Tooltip,
  LinearProgress,
  Collapse,
} from '@mui/material';
import { usersAPI } from 'services/api';
import {
  Edit,
  Delete,
  Person,
  Agriculture as Farm,
  Email,
  Phone,
  LocationOn,
  CheckCircle,
  Search,
  FilterList,
  Policy as PolicyIcon,
  Visibility,
  Refresh,
  People,
  PendingActions,
  AccountBalance,
  ExpandMore,
  ExpandLess,
  Close,
  Badge,
  Home,
  CreditCard,
  Assessment,
  Shield,
  WarningAmber,
  RestoreFromTrash,
  DeleteForever,
  LockReset,
  ContentCopy,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'success', bg: '#e8f5e9', text: '#2e7d32' },
  inactive: { label: 'Inactive', color: 'default', bg: '#f5f5f5', text: '#616161' },
  blocked: { label: 'Blocked', color: 'error', bg: '#ffebee', text: '#c62828' },
  archived: { label: 'Archived', color: 'error', bg: '#fef2f2', text: '#dc2626' },
  pending_kyc: { label: 'Pending KYC', color: 'warning', bg: '#fffbeb', text: '#d97706' },
};

const KYC_CONFIG = {
  VERIFIED: { label: 'Verified', color: 'success', bg: '#e8f5e9', text: '#2e7d32' },
  PENDING: { label: 'Pending', color: 'warning', bg: '#fff8e1', text: '#f57f17' },
  REJECTED: { label: 'Rejected', color: 'error', bg: '#ffebee', text: '#c62828' },
  NOT_SUBMITTED: { label: 'Not Submitted', color: 'default', bg: '#f5f5f5', text: '#9e9e9e' },
};

const HEADER_CELL_SX = {
  fontWeight: 700,
  fontSize: '0.7rem',
  backgroundColor: '#1a2332',
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  borderBottom: 'none',
  py: 1.5,
  px: 1.5,
  whiteSpace: 'nowrap',
};

// ═══════════════════════════════════════════════════════════════
// SUMMARY CARD COMPONENT
// ═══════════════════════════════════════════════════════════════

const SummaryCard = ({ icon, label, value, color, trend, onClick, active }) => (
  <Card
    onClick={onClick}
    sx={{
      borderRadius: 2,
      border: active ? `2px solid ${color}` : '1px solid rgba(0,0,0,0.06)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      background: active ? `${color}08` : '#fff',
      '&:hover': onClick ? {
        transform: 'translateY(-2px)',
        boxShadow: `0 4px 20px ${color}25`,
        borderColor: color,
      } : {},
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: color, lineHeight: 1 }}>
            {value}
          </Typography>
          {trend !== undefined && (
            <Typography sx={{ fontSize: '0.65rem', color: trend >= 0 ? '#22c55e' : '#ef4444', mt: 0.5 }}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% this month
            </Typography>
          )}
        </Box>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: `${color}12`,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// FARMER DETAILS COCKPIT DIALOG
// ═══════════════════════════════════════════════════════════════

const InfoRow = ({ label, value, icon }) => (
  <Box className="flex items-start gap-2 py-1.5">
    {icon && React.cloneElement(icon, { sx: { fontSize: 15, color: '#94a3b8', mt: 0.3 } })}
    <Box className="min-w-0 flex-1">
      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', mt: 0.2, wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const StatBlock = ({ label, value, color = '#1e293b', sub }) => (
  <Box className="text-center p-2 rounded-lg" sx={{ backgroundColor: `${color}08`, border: `1px solid ${color}15` }}>
    <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', mt: 0.5, letterSpacing: '0.3px' }}>
      {label}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mt: 0.3 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const CockpitSection = ({ title, icon, children, color = '#1e293b' }) => (
  <Box sx={{ mb: 2 }}>
    <Box className="flex items-center gap-1.5 mb-1.5">
      {icon && React.cloneElement(icon, { sx: { fontSize: 15, color } })}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const FarmerDetailsCockpit = ({ user, open, onClose }) => {
  if (!user) return null;

  const kycStatus = (user.kyc_status || 'NOT_SUBMITTED').toUpperCase();
  const kycConf = KYC_CONFIG[kycStatus] || KYC_CONFIG.NOT_SUBMITTED;
  const accountActive = user.is_active;

  // Parse residential address if available
  let residentialAddr = null;
  try {
    if (user.residential_address_json) residentialAddr = JSON.parse(user.residential_address_json);
  } catch { /* ignore */ }

  let farmAddr = null;
  try {
    if (user.farm_address_json) farmAddr = JSON.parse(user.farm_address_json);
  } catch { /* ignore */ }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
      sx: { borderRadius: 3, maxHeight: '92vh', background: '#f8fafc' }
    }}>
      {/* ── Header Bar ── */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', px: 3, py: 2 }}>
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-3">
            <Avatar sx={{ width: 48, height: 48, bgcolor: accountActive ? '#22c55e' : '#94a3b8', fontWeight: 700, fontSize: '1.1rem' }}>
              {(user.name || 'F')[0].toUpperCase()}
            </Avatar>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>
                {user.name}
              </Typography>
              <Box className="flex items-center gap-2 mt-0.5">
                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                  #{user.id} • {user.phone}
                </Typography>
                <Chip
                  label={kycConf.label}
                  size="small"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: kycConf.bg, color: kycConf.text }}
                />
                <Chip
                  label={accountActive ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    height: 18, fontSize: '0.6rem', fontWeight: 700,
                    bgcolor: accountActive ? '#dcfce7' : '#f1f5f9',
                    color: accountActive ? '#16a34a' : '#64748b',
                  }}
                />
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ minHeight: 480 }}>
          {/* ═══ LEFT COLUMN — Identity & Address ═══ */}
          <Grid item xs={12} md={4} sx={{ borderRight: { md: '1px solid #e2e8f0' }, p: 2.5 }}>
            <CockpitSection title="Personal Info" icon={<Person />} color="#2563eb">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <InfoRow label="Full Name" value={user.name} icon={<Person />} />
                  {user.father_name && <InfoRow label="Father's Name" value={user.father_name} icon={<Person />} />}
                  <InfoRow label="Username" value={user.username} icon={<Badge />} />
                  <InfoRow label="Mobile" value={user.phone} icon={<Phone />} />
                  {user.alternate_phone && <InfoRow label="Alt. Phone" value={user.alternate_phone} icon={<Phone />} />}
                  <InfoRow label="Email" value={user.email && !user.email.includes('@onboarding') ? user.email : '—'} icon={<Email />} />
                  {user.aadhaar_pan && <InfoRow label="Aadhaar" value={`XXXX-XXXX-${user.aadhaar_pan.slice(-4)}`} icon={<Badge />} />}
                </CardContent>
              </Card>
            </CockpitSection>

            <CockpitSection title="Residential Address" icon={<Home />} color="#d97706">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {residentialAddr ? (
                    <>
                      {residentialAddr.house_no && <InfoRow label="House No" value={residentialAddr.house_no} />}
                      {residentialAddr.village && <InfoRow label="Village" value={residentialAddr.village} />}
                      {residentialAddr.gram_panchayat && <InfoRow label="Gram Panchayat" value={residentialAddr.gram_panchayat} />}
                      <InfoRow label="Block" value={residentialAddr.block} />
                      <InfoRow label="District" value={residentialAddr.district} icon={<LocationOn />} />
                      <InfoRow label="State" value={residentialAddr.state} />
                      <InfoRow label="Pincode" value={residentialAddr.pincode} />
                    </>
                  ) : (
                    <InfoRow label="Address" value={user.address || user.kyc_address || '—'} icon={<LocationOn />} />
                  )}
                </CardContent>
              </Card>
            </CockpitSection>

            <CockpitSection title="Farm Address" icon={<Farm />} color="#059669">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {farmAddr ? (
                    <>
                      {farmAddr.same_as_residential && (
                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic', mb: 0.5 }}>
                          Same as residential
                        </Typography>
                      )}
                      {farmAddr.farm_village && <InfoRow label="Village" value={farmAddr.farm_village} />}
                      {farmAddr.farm_block && <InfoRow label="Block" value={farmAddr.farm_block} />}
                      <InfoRow label="District" value={farmAddr.farm_district} icon={<LocationOn />} />
                      {farmAddr.farm_state && <InfoRow label="State" value={farmAddr.farm_state} />}
                      {farmAddr.farm_pincode && <InfoRow label="Pincode" value={farmAddr.farm_pincode} />}
                    </>
                  ) : (
                    <InfoRow label="Farm Address" value={user.farm_address || '—'} icon={<LocationOn />} />
                  )}
                </CardContent>
              </Card>
            </CockpitSection>

            <CockpitSection title="Account & KYC Status" icon={<Shield />} color="#7c3aed">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box className="flex items-center justify-between py-1">
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Account</Typography>
                    <Chip
                      label={accountActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={accountActive ? 'success' : 'default'}
                      sx={{ fontWeight: 700, height: 22 }}
                    />
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box className="flex items-center justify-between py-1">
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>KYC Status</Typography>
                    <Chip label={kycConf.label} size="small" sx={{ fontWeight: 700, height: 22, bgcolor: kycConf.bg, color: kycConf.text }} />
                  </Box>
                  {user.kyc_verification_status && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Box className="flex items-center justify-between py-1">
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Verification</Typography>
                        <Chip
                          label={user.kyc_verification_status}
                          size="small"
                          sx={{
                            fontWeight: 700, height: 22,
                            bgcolor: user.kyc_verification_status === 'APPROVED' ? '#dcfce7' : user.kyc_verification_status === 'REJECTED' ? '#fef2f2' : '#fffbeb',
                            color: user.kyc_verification_status === 'APPROVED' ? '#16a34a' : user.kyc_verification_status === 'REJECTED' ? '#dc2626' : '#d97706',
                          }}
                        />
                      </Box>
                    </>
                  )}
                  {user.kyc_rejection_reason && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ py: 0.5 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>Rejection Reason:</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#991b1b' }}>{user.kyc_rejection_reason}</Typography>
                      </Box>
                    </>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box className="flex items-center justify-between py-1">
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>KYC Submitted</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                      {user.kyc_submitted_at ? new Date(user.kyc_submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box className="flex items-center justify-between py-1">
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Registered</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </CockpitSection>
          </Grid>

          {/* ═══ CENTER COLUMN — Credit & Financial ═══ */}
          <Grid item xs={12} md={4} sx={{ borderRight: { md: '1px solid #e2e8f0' }, p: 2.5 }}>
            <CockpitSection title="Credit Overview" icon={<AccountBalance />} color="#059669">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box className="grid grid-cols-2 gap-2">
                    <StatBlock label="Credit Limit" value={user.credit_limit ? `₹${(user.credit_limit / 1000).toFixed(0)}K` : '—'} color="#059669" />
                    <StatBlock label="Utilized" value={user.credit_utilized ? `₹${(user.credit_utilized / 1000).toFixed(0)}K` : '—'} color="#d97706" />
                  </Box>
                  <Box className="mt-2">
                    <Box className="flex justify-between mb-0.5">
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>Utilization</Typography>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#64748b' }}>
                        {user.credit_limit && user.credit_utilized
                          ? `${((user.credit_utilized / user.credit_limit) * 100).toFixed(0)}%`
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={user.credit_limit && user.credit_utilized ? Math.min((user.credit_utilized / user.credit_limit) * 100, 100) : 0}
                      sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#059669' } }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </CockpitSection>

            <CockpitSection title="Escrow Summary" icon={<CreditCard />} color="#7c3aed">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box className="grid grid-cols-2 gap-2">
                    <StatBlock label="Total Deposited" value={user.escrow_deposited ? `₹${(user.escrow_deposited / 1000).toFixed(0)}K` : '—'} color="#7c3aed" />
                    <StatBlock label="Settled" value={user.escrow_settled ? `₹${(user.escrow_settled / 1000).toFixed(0)}K` : '—'} color="#2563eb" />
                  </Box>
                </CardContent>
              </Card>
            </CockpitSection>

            <CockpitSection title="Settlement History" icon={<Assessment />} color="#0891b2">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box className="space-y-1.5">
                    <Box className="flex items-center justify-between py-1">
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Total Settlements</Typography>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                        {user.settlements_count || 0}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box className="flex items-center justify-between py-1">
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Last Settlement</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                        {user.last_settlement_date
                          ? new Date(user.last_settlement_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                          : '—'}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box className="flex items-center justify-between py-1">
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Pending Payments</Typography>
                      <Chip
                        label={user.pending_payments || 0}
                        size="small"
                        color={user.pending_payments > 0 ? 'warning' : 'default'}
                        sx={{ fontWeight: 700, height: 20 }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </CockpitSection>
          </Grid>

          {/* ═══ RIGHT COLUMN — Farm, Policy, Risk ═══ */}
          <Grid item xs={12} md={4} sx={{ p: 2.5 }}>
            <CockpitSection title="Farms & Ponds" icon={<Farm />} color="#2563eb">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box className="grid grid-cols-2 gap-2 mb-2">
                    <StatBlock label="Farms" value={user.farms_count || 0} color="#2563eb" />
                    <StatBlock label="Ponds" value={user.ponds_count || 0} color="#0891b2" />
                  </Box>
                  <Box className="grid grid-cols-2 gap-2">
                    <StatBlock label="TFA (ha)" value={user.total_tfa ? user.total_tfa.toFixed(2) : '0.00'} color="#059669" sub="Total Farm Area" />
                    <StatBlock label="WSA (ha)" value={user.total_wsa ? user.total_wsa.toFixed(2) : '0.00'} color="#7c3aed" sub="Water Spread Area" />
                  </Box>
                </CardContent>
              </Card>
            </CockpitSection>

            <CockpitSection title="Policies & Claims" icon={<PolicyIcon />} color="#d97706">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box className="grid grid-cols-2 gap-2">
                    <StatBlock label="Policies" value={user.policies_count || 0} color="#2563eb" />
                    <StatBlock label="Claims" value={user.claims_count || 0} color="#d97706" />
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box className="space-y-1">
                    <Box className="flex items-center justify-between">
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>Active Policies</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#16a34a' }}>
                        {user.active_policies || 0}
                      </Typography>
                    </Box>
                    <Box className="flex items-center justify-between">
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>Open Claims</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#d97706' }}>
                        {user.open_claims || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </CockpitSection>

            <CockpitSection title="Risk Indicators" icon={<WarningAmber />} color="#dc2626">
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {(() => {
                    // Simple risk scoring based on available data
                    const risks = [];
                    if (!accountActive) risks.push({ label: 'Account Inactive', severity: 'high' });
                    if (kycStatus === 'PENDING') risks.push({ label: 'KYC Pending', severity: 'medium' });
                    if (kycStatus === 'REJECTED') risks.push({ label: 'KYC Rejected', severity: 'high' });
                    if ((user.claims_count || 0) > (user.policies_count || 0)) risks.push({ label: 'High Claim Ratio', severity: 'high' });
                    if ((user.farms_count || 0) === 0) risks.push({ label: 'No Farms Registered', severity: 'low' });

                    if (risks.length === 0) {
                      return (
                        <Box className="flex items-center gap-1.5 py-2 justify-center">
                          <CheckCircle sx={{ color: '#22c55e', fontSize: 18 }} />
                          <Typography sx={{ fontSize: '0.8rem', color: '#22c55e', fontWeight: 600 }}>
                            No Risk Flags
                          </Typography>
                        </Box>
                      );
                    }

                    const severityColor = { high: '#dc2626', medium: '#d97706', low: '#94a3b8' };
                    return (
                      <Box className="space-y-1.5">
                        {risks.map((r, i) => (
                          <Box key={i} className="flex items-center gap-2">
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: severityColor[r.severity], flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.75rem', color: severityColor[r.severity], fontWeight: 600 }}>
                              {r.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            </CockpitSection>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const UserManagement = () => {
  const { user: _authUser } = useAuth(); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Dialogs
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', role: 'farmer', is_active: true,
    address: '', state: '', district: '', pincode: '',
  });

  // Purge dialog
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purgeTargetUser, setPurgeTargetUser] = useState(null);
  const [purgeReason, setPurgeReason] = useState('');
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);

  // Reset Password dialog
  const [resetPwdDialogOpen, setResetPwdDialogOpen] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwdResult, setResetPwdResult] = useState(null);
  const [resetPwdLoading, setResetPwdLoading] = useState(false);

  // ── Data Loading ──
  const loadUsers = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const response = await usersAPI.getAll(showDeleted);
      const apiUsers = response.data || response;

      const transformedUsers = Array.isArray(apiUsers) ? apiUsers.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        phone: u.phone || 'N/A',
        alternate_phone: u.alternate_phone,
        role: u.role,
        is_active: u.is_active !== undefined ? u.is_active : true,
        is_verified: u.is_verified || false,
        address: u.address || '',
        kyc_address: u.kyc_address || '',
        farm_address: u.farm_address || '',
        residential_address_json: u.residential_address_json || null,
        farm_address_json: u.farm_address_json || null,
        state: u.state || '',
        district: u.district || '',
        pincode: u.pincode || '',
        created_at: u.created_at,
        last_login: u.updated_at,
        farms_count: u.farms_count || 0,
        ponds_count: u.ponds_count || 0,
        policies_count: u.policies_count || 0,
        claims_count: u.claims_count || 0,
        total_tfa: u.total_tfa || 0,
        total_wsa: u.total_wsa || 0,
        username: u.username,
        father_name: u.father_name,
        aadhaar_pan: u.aadhaar_pan || u.aadhaar_number,
        pan_number: u.pan_number,
        kyc_status: u.kyc_status || 'NOT_SUBMITTED',
        account_status: u.account_status || (u.is_active ? 'ACTIVE' : 'INACTIVE'),
        kyc_submitted_at: u.kyc_submitted_at || null,
        kyc_verification_status: u.kyc_verification_status || null,
        kyc_rejection_reason: u.kyc_rejection_reason || null,
        kyc_document_front_url: u.kyc_document_front_url || null,
        kyc_document_back_url: u.kyc_document_back_url || null,
        credit_limit: u.credit_limit || 0,
        credit_utilized: u.credit_utilized || 0,
        active_policies: u.active_policies || 0,
        open_claims: u.open_claims || 0,
        pond_size: u.pond_size,
        is_deleted: u.is_deleted || false,
        deleted_at: u.deleted_at || null,
      })) : [];

      setUsers(transformedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.detail || 'Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showDeleted]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Derived Data ──
  const farmerUsers = useMemo(() => users.filter(u => u.role === 'farmer'), [users]);

  const districts = useMemo(() => {
    const set = new Set();
    farmerUsers.forEach(u => {
      // Try parsing residential_address_json for district
      let district = u.district;
      if (!district && u.residential_address_json) {
        try { district = JSON.parse(u.residential_address_json).district; } catch { /* ignore */ }
      }
      if (district) set.add(district);
    });
    return [...set].sort();
  }, [farmerUsers]);

  const stats = useMemo(() => {
    const active = farmerUsers.filter(u => u.is_active && !u.is_deleted).length;
    const archived = farmerUsers.filter(u => u.is_deleted).length;
    const kycPending = farmerUsers.filter(u => (u.kyc_status || '').toUpperCase() === 'PENDING').length;
    const creditActive = farmerUsers.filter(u => u.credit_limit > 0).length;
    const highRisk = farmerUsers.filter(u => {
      const ks = (u.kyc_status || '').toUpperCase();
      return !u.is_active || ks === 'REJECTED' || (u.claims_count || 0) > (u.policies_count || 0);
    }).length;
    return {
      total: farmerUsers.length,
      active,
      archived,
      kycPending,
      creditActive,
      highRisk,
    };
  }, [farmerUsers]);

  const filteredUsers = useMemo(() => {
    return farmerUsers.filter(u => {
      // Deleted filter
      if (statusFilter === 'deleted') {
        if (!u.is_deleted) return false;
      } else {
        // In non-deleted views, hide deleted users unless showDeleted is on
        if (u.is_deleted && statusFilter !== 'all') return false;
      }
      // Search
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const match = (u.name || '').toLowerCase().includes(q) ||
          (u.phone || '').includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          String(u.id).includes(q);
        if (!match) return false;
      }
      // Status
      if (statusFilter === 'active' && (!u.is_active || u.is_deleted)) return false;
      if (statusFilter === 'inactive' && (u.is_active || u.is_deleted)) return false;
      // KYC
      if (kycFilter !== 'all') {
        const ks = (u.kyc_status || 'NOT_SUBMITTED').toUpperCase();
        if (kycFilter === 'verified' && ks !== 'VERIFIED') return false;
        if (kycFilter === 'pending' && ks !== 'PENDING') return false;
        if (kycFilter === 'rejected' && ks !== 'REJECTED') return false;
      }
      // District
      if (districtFilter !== 'all') {
        let district = u.district;
        if (!district && u.residential_address_json) {
          try { district = JSON.parse(u.residential_address_json).district; } catch { /* ignore */ }
        }
        if (district !== districtFilter) return false;
      }
      return true;
    });
  }, [farmerUsers, searchTerm, statusFilter, kycFilter, districtFilter]);

  const paginatedUsers = useMemo(
    () => filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  // ── Handlers ──
  const handleViewUser = (u) => { setSelectedUser(u); setViewDialogOpen(true); };
  const handleCloseViewDialog = () => { setViewDialogOpen(false); setSelectedUser(null); };

  const handleOpenDialog = (u = null) => {
    if (u) {
      setEditingUser(u);
      setFormData({ name: u.name || '', email: u.email || '', phone: u.phone || '', role: u.role || 'farmer', is_active: u.is_active !== undefined ? u.is_active : true, address: u.address || '', state: u.state || '', district: u.district || '', pincode: u.pincode || '' });
    } else {
      setEditingUser(null);
      setFormData({ name: '', email: '', phone: '', role: 'farmer', is_active: true, address: '', state: '', district: '', pincode: '' });
    }
    setOpenDialog(true);
  };
  const handleCloseDialog = () => { setOpenDialog(false); setEditingUser(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      if (editingUser) {
        setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...formData } : u));
      } else {
        const newUser = { id: Math.max(...users.map(u => u.id), 0) + 1, ...formData, created_at: new Date().toISOString(), farms_count: 0, policies_count: 0, claims_count: 0 };
        setUsers([...users, newUser]);
      }
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save user.');
    }
  };

  const handleDelete = async (userId) => {
    const u = users.find(x => x.id === userId);
    if (window.confirm(`Deactivate and soft-delete ${u?.name || 'this user'}? The user can be restored later.`)) {
      try {
        setLoading(true);
        await usersAPI.delete(userId);
        // Mark as deleted in local state (or remove from list if not showing deleted)
        if (showDeleted) {
          setUsers(prev => prev.map(x => x.id === userId ? { ...x, is_deleted: true, is_active: false, deleted_at: new Date().toISOString() } : x));
        } else {
          setUsers(prev => prev.filter(x => x.id !== userId));
        }
        setSuccess(`User ${u?.name || userId} soft-deleted`);
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete user.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestore = async (userId) => {
    const u = users.find(x => x.id === userId);
    if (window.confirm(`Restore ${u?.name || 'this user'}? They will need KYC re-verification.`)) {
      try {
        setLoading(true);
        await usersAPI.restore(userId);
        setUsers(prev => prev.map(x => x.id === userId ? { ...x, is_deleted: false, is_active: true, deleted_at: null, account_status: 'PENDING_KYC', kyc_status: 'PENDING' } : x));
        setSuccess(`User ${u?.name || userId} restored — status set to Pending KYC`);
        setTimeout(() => setSuccess(''), 4000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to restore user.');
      } finally {
        setLoading(false);
      }
    }
  };

  const openPurgeDialog = (userId) => {
    const u = users.find(x => x.id === userId);
    setPurgeTargetUser(u);
    setPurgeReason('');
    setPurgeConfirmText('');
    setPurgeDialogOpen(true);
  };

  const closePurgeDialog = () => {
    setPurgeDialogOpen(false);
    setPurgeTargetUser(null);
    setPurgeReason('');
    setPurgeConfirmText('');
  };

  const handlePurgeConfirm = async () => {
    if (!purgeTargetUser || purgeConfirmText !== 'PURGE' || !purgeReason.trim()) return;
    try {
      setPurgeLoading(true);
      await usersAPI.purge(purgeTargetUser.id, purgeReason.trim());
      setUsers(prev => prev.filter(x => x.id !== purgeTargetUser.id));
      setSuccess(`User ${purgeTargetUser.name || purgeTargetUser.id} permanently purged from the system`);
      setTimeout(() => setSuccess(''), 4000);
      closePurgeDialog();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.message) {
        const records = detail.financial_records || {};
        const breakdown = Object.entries(records).map(([k, v]) => `${k}: ${v}`).join(', ');
        setError(`${detail.message} (${breakdown})`);
      } else {
        setError(detail || 'Failed to purge user.');
      }
      closePurgeDialog();
    } finally {
      setPurgeLoading(false);
    }
  };

  // ── Reset Password ──
  const openResetPwdDialog = (userId) => {
    const u = users.find(x => x.id === userId);
    setResetPwdUser(u);
    setResetPwdResult(null);
    setResetPwdDialogOpen(true);
  };

  const closeResetPwdDialog = () => {
    setResetPwdDialogOpen(false);
    setResetPwdUser(null);
    setResetPwdResult(null);
  };

  const handleResetPassword = async () => {
    if (!resetPwdUser) return;
    try {
      setResetPwdLoading(true);
      const response = await usersAPI.resetPassword(resetPwdUser.id);
      const data = response.data || response;
      setResetPwdResult(data);
      setSuccess(`Password reset for ${resetPwdUser.name}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to reset password.';
      setError(detail);
      closeResetPwdDialog();
    } finally {
      setResetPwdLoading(false);
    }
  };

  const handleToggleActive = useCallback((userId) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !u.is_active } : u));
  }, []);

  const getKycChip = (kycStatus) => {
    const ks = (kycStatus || 'NOT_SUBMITTED').toUpperCase();
    const conf = KYC_CONFIG[ks] || KYC_CONFIG.NOT_SUBMITTED;
    return <Chip label={conf.label} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', bgcolor: conf.bg, color: conf.text }} />;
  };

  const getStatusChip = (isActive, isDeleted, accountStatus) => {
    if (isDeleted) {
      return <Chip label="Archived" size="small" icon={<DeleteForever sx={{ fontSize: 12 }} />}
        sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#dc2626', '& .MuiChip-icon': { color: '#dc2626' } }} />;
    }
    const as = (accountStatus || '').toUpperCase();
    if (as === 'PENDING_KYC') {
      return <Chip label="Pending KYC" size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', bgcolor: '#fffbeb', color: '#d97706' }} />;
    }
    if (as === 'BLOCKED') {
      return <Chip label="Blocked" size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', bgcolor: '#ffebee', color: '#c62828' }} />;
    }
    const conf = isActive ? STATUS_CONFIG.active : STATUS_CONFIG.inactive;
    return <Chip label={conf.label} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', bgcolor: conf.bg, color: conf.text }} />;
  };

  const getUserDistrict = (u) => {
    if (u.district) return u.district;
    if (u.residential_address_json) {
      try { return JSON.parse(u.residential_address_json).district || '—'; } catch { /* ignore */ }
    }
    return '—';
  };

  // ── Loading State ──
  if (loading && users.length === 0) {
    return (
      <Box className="flex flex-col justify-center items-center" sx={{ height: '60vh' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography sx={{ color: '#64748b', fontWeight: 500 }}>Loading farmer data…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 3 } }}>
      {/* ── Progress bar on refresh ── */}
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ══════════ SUMMARY CARDS ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <SummaryCard
          icon={<People />} label="Total Farmers" value={stats.total}
          color="#2563eb" onClick={() => { setStatusFilter('all'); setKycFilter('all'); }}
          active={statusFilter === 'all' && kycFilter === 'all'}
        />
        <SummaryCard
          icon={<CheckCircle />} label="Active" value={stats.active}
          color="#16a34a" onClick={() => { setStatusFilter('active'); setKycFilter('all'); }}
          active={statusFilter === 'active'}
        />
        <SummaryCard
          icon={<PendingActions />} label="KYC Pending" value={stats.kycPending}
          color="#d97706" onClick={() => { setStatusFilter('all'); setKycFilter('pending'); }}
          active={kycFilter === 'pending'}
        />
        <SummaryCard
          icon={<AccountBalance />} label="Credit Active" value={stats.creditActive}
          color="#7c3aed"
        />
        <SummaryCard
          icon={<DeleteForever />} label="Archived" value={stats.archived}
          color="#dc2626"
          onClick={() => { setStatusFilter('deleted'); setKycFilter('all'); if (!showDeleted) setShowDeleted(true); }}
          active={statusFilter === 'deleted'}
        />
        <SummaryCard
          icon={<WarningAmber />} label="High Risk" value={stats.highRisk}
          color="#ef4444"
        />
      </Box>

      {/* ══════════ TOOLBAR ══════════ */}
      <Card sx={{ borderRadius: 2, mb: 2, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search name, phone, ID…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
              }}
            />

            {/* Quick filter toggles */}
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterList />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 100 }}
            >
              Filters {showFilters ? <ExpandLess sx={{ ml: 0.5 }} /> : <ExpandMore sx={{ ml: 0.5 }} />}
            </Button>

            <Box className="flex-1" />

            {/* Refresh */}
            <Tooltip title="Refresh data">
              <IconButton onClick={() => loadUsers(true)} disabled={refreshing} size="small">
                <Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Count */}
            <Chip
              label={`${filteredUsers.length} of ${farmerUsers.length} farmers`}
              size="small"
              sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }}
            />
          </Box>

          {/* ── Advanced Filters Row ── */}
          <Collapse in={showFilters}>
            <Box className="flex flex-wrap items-center gap-2 mt-2 pt-2" sx={{ borderTop: '1px solid #e2e8f0' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => {
                    const val = e.target.value;
                    setStatusFilter(val);
                    setPage(0);
                    if (val === 'deleted' && !showDeleted) { setShowDeleted(true); }
                  }}
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="deleted">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeleteForever sx={{ fontSize: 14, color: '#dc2626' }} />
                      Deleted Users
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>KYC Status</InputLabel>
                <Select value={kycFilter} label="KYC Status" onChange={(e) => { setKycFilter(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All KYC</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>District</InputLabel>
                <Select value={districtFilter} label="District" onChange={(e) => { setDistrictFilter(e.target.value); setPage(0); }}
                  sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <MenuItem value="all">All Districts</MenuItem>
                  {districts.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>

              {(statusFilter !== 'all' || kycFilter !== 'all' || districtFilter !== 'all' || searchTerm) && (
                <Button
                  size="small"
                  onClick={() => { setStatusFilter('all'); setKycFilter('all'); setDistrictFilter('all'); setSearchTerm(''); setShowDeleted(false); setPage(0); }}
                  sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600 }}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* ══════════ DATA TABLE ══════════ */}
      <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '4%' }}>#</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '20%' }}>Farmer</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '12%' }}>Contact</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '12%' }}>District</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '8%', textAlign: 'center' }}>TFA</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '8%', textAlign: 'center' }}>WSA</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '9%', textAlign: 'center' }}>Status</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '9%', textAlign: 'center' }}>KYC</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '8%', textAlign: 'center' }}>Farms</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: '10%', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 8, textAlign: 'center' }}>
                    <Farm sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ color: '#94a3b8', fontWeight: 600, mb: 0.5 }}>No Farmers Found</Typography>
                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.8rem' }}>
                      {searchTerm || statusFilter !== 'all' || kycFilter !== 'all' ? 'Adjust filters to see results' : 'No farmer records available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((u, idx) => (
                  <TableRow
                    key={u.id}
                    hover
                    onClick={() => handleViewUser(u)}
                    sx={{
                      cursor: 'pointer',
                      height: 48,
                      borderLeft: `3px solid ${u.is_deleted ? '#dc2626' : u.is_active ? '#22c55e' : '#e2e8f0'}`,
                      bgcolor: u.is_deleted ? '#fef2f2' : (idx % 2 === 0 ? '#fff' : '#fafbfc'),
                      opacity: u.is_deleted ? 0.75 : 1,
                      '&:hover': { bgcolor: '#f0f7ff !important' },
                      transition: 'background 0.15s ease',
                    }}
                  >
                    <TableCell sx={{ py: 0.8, px: 1.5, color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600 }}>
                      {page * rowsPerPage + idx + 1}
                    </TableCell>
                    <TableCell sx={{ py: 0.8, px: 1.5 }}>
                      <Box className="flex items-center gap-1.5">
                        <Avatar sx={{ width: 28, height: 28, bgcolor: u.is_active ? '#dbeafe' : '#f1f5f9', color: u.is_active ? '#2563eb' : '#94a3b8', fontSize: '0.7rem', fontWeight: 700 }}>
                          {(u.name || 'F')[0].toUpperCase()}
                        </Avatar>
                        <Box className="min-w-0">
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.name}
                          </Typography>
                          {u.father_name && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', lineHeight: 1.2 }}>
                              S/O: {u.father_name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#1e293b', lineHeight: 1.2 }}>{u.phone}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getUserDistrict(u)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#2563eb' }}>
                        {u.total_tfa ? u.total_tfa.toFixed(1) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0891b2' }}>
                        {u.total_wsa ? u.total_wsa.toFixed(1) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={u.is_deleted ? 'Archived (soft-deleted)' : u.is_active ? 'Click to deactivate' : 'Click to activate'}>
                        <Box sx={{ cursor: u.is_deleted ? 'default' : 'pointer', display: 'inline-block' }} onClick={() => !u.is_deleted && handleToggleActive(u.id)}>
                          {getStatusChip(u.is_active, u.is_deleted, u.account_status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                      {getKycChip(u.kyc_status)}
                    </TableCell>
                    <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                      <Box className="flex items-center justify-center gap-1">
                        <Farm sx={{ fontSize: 14, color: '#2563eb' }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>
                          {u.farms_count}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          /{u.ponds_count}p
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <Box className="flex items-center justify-center gap-0.5">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewUser(u)} sx={{ color: '#2563eb' }}>
                            <Visibility sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        {u.is_deleted ? (
                          <>
                            <Tooltip title="Restore User (sets Pending KYC)">
                              <IconButton size="small" onClick={() => handleRestore(u.id)} sx={{ color: '#16a34a' }}>
                                <RestoreFromTrash sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Permanently Purge User">
                              <IconButton size="small" onClick={() => openPurgeDialog(u.id)} sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}>
                                <DeleteForever sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenDialog(u)} sx={{ color: '#64748b' }}>
                                <Edit sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset Password">
                              <IconButton size="small" onClick={() => openResetPwdDialog(u.id)} sx={{ color: '#d97706' }}>
                                <LockReset sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(u.id)} sx={{ color: '#ef4444' }}>
                                <Delete sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 15, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Card>

      {/* ══════════ FARMER DETAILS COCKPIT ══════════ */}
      <FarmerDetailsCockpit user={selectedUser} open={viewDialogOpen} onClose={handleCloseViewDialog} />

      {/* ══════════ ADD/EDIT DIALOG (kept simple) ══════════ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: '#0f172a', color: '#fff', fontWeight: 700 }}>
          {editingUser ? 'Edit Farmer' : 'Add New Farmer'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" size="small" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" type="email" size="small" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" size="small" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" size="small" value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="State" size="small" value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="District" size="small" value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Pincode" size="small" value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <Box className="flex items-center">
                  <Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} color="success" />
                  <Typography variant="body2" sx={{ ml: 1 }}>Active User</Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
          RESET PASSWORD DIALOG
      ═══════════════════════════════════════════════════════════ */}
      <Dialog
        open={resetPwdDialogOpen}
        onClose={closeResetPwdDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box sx={{ bgcolor: '#d97706', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockReset sx={{ color: '#fff', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, flex: 1 }}>
            Reset Password
          </Typography>
          <IconButton onClick={closeResetPwdDialog} size="small" sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {!resetPwdResult ? (
            <>
              <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                This will generate a new temporary password. The user will be required to change it on first login.
              </Alert>
              {resetPwdUser && (
                <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2, p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                    User: <strong>{resetPwdUser.name}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#78350f' }}>
                    {resetPwdUser.phone} &middot; {resetPwdUser.email}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2, fontWeight: 700 }}>
                Password reset successfully!
              </Alert>
              <Box sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2, p: 2.5, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 700, mb: 1 }}>
                  Temporary Password:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', border: '1px solid #d1d5db', borderRadius: 2, px: 2, py: 1.5 }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', letterSpacing: 1.5, flex: 1 }}>
                    {resetPwdResult.temporary_password}
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(resetPwdResult.temporary_password);
                        setSuccess('Password copied to clipboard');
                        setTimeout(() => setSuccess(''), 2000);
                      }}
                      sx={{ color: '#2563eb' }}
                    >
                      <ContentCopy sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" sx={{ color: '#15803d', mt: 1.5, display: 'block' }}>
                  Share this password securely with the user. They will be prompted to change it on first login.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={closeResetPwdDialog} sx={{ textTransform: 'none', color: '#64748b' }}>
            {resetPwdResult ? 'Close' : 'Cancel'}
          </Button>
          {!resetPwdResult && (
            <Button
              variant="contained"
              onClick={handleResetPassword}
              disabled={resetPwdLoading}
              startIcon={resetPwdLoading ? <CircularProgress size={16} color="inherit" /> : <LockReset />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 2,
                bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' },
              }}
            >
              {resetPwdLoading ? 'Resetting...' : 'Generate New Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
          PURGE CONFIRMATION DIALOG
      ═══════════════════════════════════════════════════════════ */}
      <Dialog
        open={purgeDialogOpen}
        onClose={closePurgeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* Red warning header */}
        <Box sx={{ bgcolor: '#dc2626', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DeleteForever sx={{ color: '#fff', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, flex: 1 }}>
            Permanent Purge
          </Typography>
          <IconButton onClick={closePurgeDialog} size="small" sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {/* Warning banner */}
          <Alert severity="error" variant="filled" sx={{ mb: 2.5, fontWeight: 700, borderRadius: 2, '& .MuiAlert-icon': { fontSize: 22 } }}>
            This action is irreversible. The user and all non-financial records will be permanently removed.
          </Alert>

          {/* User info */}
          {purgeTargetUser && (
            <Box sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2, p: 2, mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b', mb: 0.5 }}>
                User to be purged:
              </Typography>
              <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                <strong>{purgeTargetUser.name}</strong> &middot; {purgeTargetUser.phone} &middot; {purgeTargetUser.email}
              </Typography>
              <Typography variant="caption" sx={{ color: '#b91c1c', mt: 0.5, display: 'block' }}>
                This will delete: KYC documents, onboarding records, farms, ponds, and the user account.
              </Typography>
            </Box>
          )}

          {/* Reason input */}
          <TextField
            fullWidth
            label="Reason for purge *"
            placeholder="E.g. Duplicate account, test data cleanup, user request..."
            multiline
            rows={2}
            value={purgeReason}
            onChange={(e) => setPurgeReason(e.target.value)}
            sx={{ mb: 2.5 }}
            InputProps={{ sx: { borderRadius: 2 } }}
          />

          {/* Confirmation input */}
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
            Type <strong style={{ color: '#dc2626' }}>PURGE</strong> below to confirm permanent deletion:
          </Typography>
          <TextField
            fullWidth
            placeholder="Type PURGE to confirm"
            value={purgeConfirmText}
            onChange={(e) => setPurgeConfirmText(e.target.value.toUpperCase())}
            error={purgeConfirmText.length > 0 && purgeConfirmText !== 'PURGE'}
            helperText={purgeConfirmText.length > 0 && purgeConfirmText !== 'PURGE' ? 'Must type exactly "PURGE"' : ''}
            InputProps={{ sx: { borderRadius: 2, fontWeight: 700, letterSpacing: 2 } }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button onClick={closePurgeDialog} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePurgeConfirm}
            disabled={purgeConfirmText !== 'PURGE' || !purgeReason.trim() || purgeLoading}
            startIcon={purgeLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: '#dc2626',
              '&:hover': { bgcolor: '#b91c1c' },
              '&.Mui-disabled': { bgcolor: '#fecaca', color: '#f87171' },
            }}
          >
            {purgeLoading ? 'Purging...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
